import { addDays, differenceInCalendarDays, format } from 'date-fns';
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Modal } from '../components/Modal';

export type UrlTemplateDateFormatEntry = {
  queryParam?: string;
  dateFormat?: string;
  options?: ReadonlyArray<string>;
  type?: 'ARRIVAL' | 'DEPARTURE' | 'LOS';
};

type UrlTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  urlTemplate?: string;
  dateFormatToken?: string;
  dateFormats?: ReadonlyArray<UrlTemplateDateFormatEntry>;
  localHotelId?: string;
};

export function UrlTemplateModal({
  isOpen,
  onClose,
  urlTemplate,
  dateFormatToken,
  dateFormats,
  localHotelId,
}: UrlTemplateModalProps): ReactElement | null {
  const [arrivalDateInput, setArrivalDateInput] = useState('');
  const [departureDateInput, setDepartureDateInput] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);
  const [generatedTemplateUrl, setGeneratedTemplateUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const today = new Date();
    const departure = addDays(today, 1);

    setArrivalDateInput(format(today, 'yyyy-MM-dd'));
    setDepartureDateInput(format(departure, 'yyyy-MM-dd'));
    setGeneratedTemplateUrl(null);
    setModalError(null);
  }, [isOpen, urlTemplate, dateFormatToken, dateFormats]);

  function generateTemplateUrl(): void {
    if (typeof urlTemplate !== 'string' || !urlTemplate.trim()) {
      setModalError('urlTemplate is missing for this hotel.');
      setGeneratedTemplateUrl(null);
      return;
    }

    if (!arrivalDateInput || !departureDateInput) {
      setModalError('Choose both arrival and departure dates.');
      setGeneratedTemplateUrl(null);
      return;
    }

    const arrivalDate = new Date(`${arrivalDateInput}T00:00:00`);
    const departureDate = new Date(`${departureDateInput}T00:00:00`);
    if (Number.isNaN(arrivalDate.getTime()) || Number.isNaN(departureDate.getTime())) {
      setModalError('One or both selected dates are invalid.');
      setGeneratedTemplateUrl(null);
      return;
    }

    const usableDateFormats = dateFormats ?? [];

    if (
      usableDateFormats.length === 0 &&
      (typeof dateFormatToken !== 'string' || !dateFormatToken.trim())
    ) {
      setModalError('dateFormat is missing for this hotel.');
      setGeneratedTemplateUrl(null);
      return;
    }

    try {
      const localHotelIdValue =
        typeof localHotelId === 'string' && localHotelId.trim() ? localHotelId.trim() : '';
      let generatedUrl = urlTemplate.split('${local_hotel_id}').join(localHotelIdValue);

      if (usableDateFormats.length > 0) {
        for (const entry of usableDateFormats) {
          // NAR: eventually support zero based dates. We should take the formatting implementation from the rate-scrape repo
          const formattedValue =
            entry.type === 'LOS'
              ? String(differenceInCalendarDays(departureDate, arrivalDate))
              : format(entry.type === 'DEPARTURE' ? departureDate : arrivalDate, entry.dateFormat!);
          generatedUrl = generatedUrl.split(`\${${entry.queryParam}}`).join(formattedValue);
        }
      } else if (typeof dateFormatToken === 'string') {
        const formattedArrival = format(arrivalDate, dateFormatToken);
        const formattedDeparture = format(departureDate, dateFormatToken);
        generatedUrl = generatedUrl
          .split('${arrival_date}')
          .join(formattedArrival)
          .split('${departure_date}')
          .join(formattedDeparture);
      }

      setGeneratedTemplateUrl(generatedUrl);
      setModalError(null);
    } catch {
      setModalError('dateFormat is invalid for date-fns formatting.');
      setGeneratedTemplateUrl(null);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    generateTemplateUrl();
  }, [
    arrivalDateInput,
    dateFormatToken,
    dateFormats,
    departureDateInput,
    isOpen,
    localHotelId,
    urlTemplate,
  ]);

  function handleClose(): void {
    setModalError(null);
    setGeneratedTemplateUrl(null);
    onClose();
  }

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <>
        <h4 className="text-sm font-semibold text-slate-900">Build URL From Template</h4>
        <p className="mt-1 text-xs text-slate-600">
          {dateFormats && dateFormats.length > 0 ? (
            <>Choose dates to replace each query param token (e.g. {'${arrivalDate}'}).</>
          ) : (
            <>
              Choose dates to replace <code>${'{arrival_date}'}</code> and{' '}
              <code>${'{departure_date}'}</code>.
            </>
          )}
        </p>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-xs text-slate-700">
            Arrival
            <input
              type="date"
              value={arrivalDateInput}
              onChange={(event) => setArrivalDateInput(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
            />
          </label>

          <label className="text-xs text-slate-700">
            Departure
            <input
              type="date"
              value={departureDateInput}
              onChange={(event) => setDepartureDateInput(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
            />
          </label>
        </div>

        {modalError ? <p className="mt-3 text-xs text-rose-700">{modalError}</p> : null}

        {generatedTemplateUrl ? (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2">
            <p className="text-xs font-medium text-slate-700">Generated URL</p>
            <a
              href={generatedTemplateUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 block break-all text-xs text-sky-700 underline"
            >
              {generatedTemplateUrl}
            </a>
          </div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
        </div>
      </>
    </Modal>
  );
}
